import { faStickyNote } from '@fortawesome/pro-solid-svg-icons';
import { Popover, PopoverButton } from '@headlessui/react';
import { FC, forwardRef, useCallback, useMemo, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { ControlPanelButton, ControlPanelButtonTooltip } from '../../components/ControlPanelButton';
import { PopoverChevronIndicator } from '../../components/popover/PopoverChevronIndicator';
import { PopoverPanel } from '../../components/popover/PopoverPanel';
import { useKeyPressEvent } from '../../hooks/useKeyPressEvent';
import { capitalize, mergeRefs } from '../../utils';

const PatientNotePopoverButton = forwardRef<HTMLButtonElement, { isOpen: boolean }>(function PatientNotePopoverButton(
  { isOpen, ...props },
  ref,
) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const mergedRefs = useMemo(() => mergeRefs<HTMLButtonElement>(ref, buttonRef), [ref, buttonRef]);

  /**
   * Unfortunately there's no way to programmatically open and close the
   * popover. Our workaround is to "click" the button to toggle it.
   */
  const togglePopover = useCallback(() => {
    buttonRef.current?.click();
  }, []);

  useKeyPressEvent('N', togglePopover);

  const actionName = isOpen ? 'close' : 'open';

  return (
    <ControlPanelButton
      {...props}
      ref={mergedRefs}
      aria-label={`${capitalize(actionName)} sticky note. Pressing shift + n will also ${actionName} the sticky note.`}
      iconDefinition={faStickyNote}
    >
      <PopoverChevronIndicator isOpen={isOpen} />
    </ControlPanelButton>
  );
});

export type PatientNotePopoverControlButtonProps = {
  patientNoteComponent: FC;
  onError?: (error: Error) => void;
};

function PatientNotePopoverErrorFallback() {
  return null;
}

export function PatientNotePopoverControlButton({
  patientNoteComponent: PatientNoteComponent,
  onError,
}: PatientNotePopoverControlButtonProps) {
  return (
    <ErrorBoundary FallbackComponent={PatientNotePopoverErrorFallback} onError={onError}>
      <Popover>
        {({ open }: { open: boolean }) => (
          <>
            <ControlPanelButtonTooltip text={`${open ? 'Close' : 'Open'} sticky note (shift + n)`} childIsInteractive>
              <PopoverButton as={PatientNotePopoverButton} isOpen={open} />
            </ControlPanelButtonTooltip>
            <PopoverPanel anchor={undefined} className='min-w-full sm:min-w-[570px] !max-w-[570px]'>
              <PatientNoteComponent />
            </PopoverPanel>
          </>
        )}
      </Popover>
    </ErrorBoundary>
  );
}
